import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useUsers,
  useDeleteUser,
  useActivateUser,
  useDeactivateUser,
  useUpdateUser,
  useUserPreferences,
  useUploadUserAvatar,
  useResendInvite,
  useResetUserPassword,
} from '../hooks';
import { UserCard, UserListItem } from '../components';
import { DeleteConfirmationModal, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, Modal, ModalHeader, ModalContent, ModalFooter, Select, Button, FilterBottomSheet, FiltersButton, PlainHeadCell, SelectAllHeadCell, SortableHeadCell, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useDropdownLevels } from '@/modules/levels/hooks/useLevels';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import type { UserQuery, UserResponse, ActiveStatus } from '@troia-v3/types';

type SortKey = 'name' | 'code' | 'level' | 'status' | 'lastLogin';

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();

  // "+" central do bottom-nav mobile → novo item (mockup .m-fab).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/settings/users/create'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);
  const { viewMode, setViewMode } = useUserPreferences();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  // 'Ver usuários' (menu de Níveis) faz deep-link ?levelId=... — inicializa o filtro a partir da URL.
  const [query, setQuery] = useState<UserQuery>(() => {
    const levelId = searchParams.get('levelId');
    return { page: 1, limit: 12, ...(levelId ? { levelId } : {}) };
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { data, isLoading, error } = useUsers(query);
  const deleteUser = useDeleteUser();
  const activateUser = useActivateUser();
  const deactivateUser = useDeactivateUser();
  const uploadAvatar = useUploadUserAvatar();
  const resendInvite = useResendInvite();
  const resetPassword = useResetUserPassword();

  // Dropdowns cross-module (rotas /dropdown sem permissão) para os filtros Nível/Equipe.
  const { data: levelsData } = useDropdownLevels();
  const { data: teamsData } = useTeamsDropdown({ enabled: filtersOpen || !!query.teamId });
  const levels = useMemo(() => levelsData ?? [], [levelsData]);
  const updateUser = useUpdateUser();
  // Mini-modal de troca de nível: { userId } (individual) ou { bulk:true } (em massa)
  const [levelModal, setLevelModal] = useState<{ userId?: string; bulk?: boolean } | null>(null);
  const [levelValue, setLevelValue] = useState('');
  const teams = useMemo(() => teamsData ?? [], [teamsData]);

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para usuários).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'pt-BR');
      else if (sort.key === 'level') cmp = (a.levelName ?? '').localeCompare(b.levelName ?? '', 'pt-BR');
      else if (sort.key === 'code') cmp = (a.code ?? '').localeCompare(b.code ?? '', 'pt-BR', { numeric: true });
      else if (sort.key === 'status') cmp = (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1);
      else cmp = new Date(a.lastLoginAt ?? 0).getTime() - new Date(b.lastLoginAt ?? 0).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((u) => selectedIds.has(u.id));
  const someOnPageSelected = items.some((u) => selectedIds.has(u.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: ActiveStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };

  const setLevelFilter = (levelId?: string) => {
    setQuery((prev) => ({ ...prev, levelId, page: 1 }));
    setFiltersOpen(false);
  };

  const setTeamFilter = (teamId?: string) => {
    setQuery((prev) => ({ ...prev, teamId, page: 1 }));
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, levelId: undefined, teamId: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((u) => next.delete(u.id));
      else items.forEach((u) => next.add(u.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const openChangeLevel = (id: string) => { setLevelValue(''); setLevelModal({ userId: id }); };
  const openBulkChangeLevel = () => { setLevelValue(''); setLevelModal({ bulk: true }); };
  const confirmChangeLevel = () => {
    if (!levelValue || !levelModal) return;
    if (levelModal.bulk) {
      selectedIds.forEach((id) => updateUser.mutate({ id, data: { levelId: levelValue } }));
      clearSelection();
    } else if (levelModal.userId) {
      updateUser.mutate({ id: levelModal.userId, data: { levelId: levelValue } });
    }
    setLevelModal(null);
  };

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleEdit = (id: string) => navigate(`/settings/users/${id}/edit`);

  const handleDelete = (id: string) => setDeleteId(id);

  const confirmDelete = () => {
    if (deleteId) deleteUser.mutate(deleteId);
    setDeleteId(null);
  };

  const handleToggleStatus = (id: string, currentStatus: string) => {
    if (currentStatus === 'active') deactivateUser.mutate(id);
    else activateUser.mutate(id);
  };

  const handleAvatarChange = (id: string, file: File) => uploadAvatar.mutate({ id, file });

  const handleResendInvite = (id: string) => resendInvite.mutate(id);
  const handleResetPassword = (id: string) => resetPassword.mutate(id);

  const bulkResendInvite = () => {
    selectedIds.forEach((id) => resendInvite.mutate(id));
    clearSelection();
  };

  const bulkDeactivate = () => {
    const targets = items.filter((u) => selectedIds.has(u.id) && u.status === 'active');
    targets.forEach((u) => deactivateUser.mutate(u.id));
    clearSelection();
  };

  const bulkDelete = () => setBulkDeleteOpen(true);

  const confirmBulkDelete = () => {
    Array.from(selectedIds).forEach((id) => deleteUser.mutate(id));
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const statusLabel = query.status === 'active' ? 'Ativos' : query.status === 'inactive' ? 'Inativos' : null;
  const levelLabel = query.levelId ? (levels.find((l) => l.id === query.levelId)?.name ?? null) : null;
  const teamLabel = query.teamId ? (teams.find((t) => t.id === query.teamId)?.name ?? null) : null;
  const activeFilterCount = [statusLabel, levelLabel, teamLabel].filter(Boolean).length;

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {([[undefined, 'Todos'], ['active', 'Ativos'], ['inactive', 'Inativos']] as const).map(([val, label]) => {
        const active = query.status === val;
        return (
          <button
            key={label}
            onClick={() => setStatusFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2.5 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}

      {/* Nível */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Nível</p>
      <button
        onClick={() => setLevelFilter(undefined)}
        className="w-full flex items-center justify-between px-2.5 py-2.5 rounded-lg text-sm font-medium text-left"
        style={{ color: '#374151', backgroundColor: !query.levelId ? '#f3e8ff' : 'transparent' }}
        onMouseEnter={(e) => { if (query.levelId) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={(e) => { if (query.levelId) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        Todos
        {!query.levelId && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
      </button>
      {levels.map((lvl) => {
        const active = query.levelId === lvl.id;
        return (
          <button
            key={lvl.id}
            onClick={() => setLevelFilter(lvl.id)}
            className="w-full flex items-center justify-between px-2.5 py-2.5 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span className="truncate">{lvl.name}</span>
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}

      {/* Equipe */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Equipe</p>
      <button
        onClick={() => setTeamFilter(undefined)}
        className="w-full flex items-center justify-between px-2.5 py-2.5 rounded-lg text-sm font-medium text-left"
        style={{ color: '#374151', backgroundColor: !query.teamId ? '#f3e8ff' : 'transparent' }}
        onMouseEnter={(e) => { if (query.teamId) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={(e) => { if (query.teamId) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        Todas
        {!query.teamId && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
      </button>
      {teams.map((team) => {
        const active = query.teamId === team.id;
        return (
          <button
            key={team.id}
            onClick={() => setTeamFilter(team.id)}
            className="w-full flex items-center justify-between px-2.5 py-2.5 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span className="truncate">{team.name}</span>
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        chain="cl-tb"
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate('/settings')} className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Configurações
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Usuários</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          /* Centro: busca (F17-22: ToolbarSearch shared, sempre centralizada) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar por nome ou e-mail…"
            data-testid="users-search"
          />
        }
        right={<>
          {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros (F05: FiltersButton shared) */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="users-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: ViewToggle shared — Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="users-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo usuário (único primário — F01-03: Button shared com colapso) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate('/settings/users/create')}
            data-testid="new-user"
          >
            Novo usuário
          </Button>
        </>}
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar usuário…"
              data-testid="users-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="users-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {levelLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Nível: {levelLabel}
              <button onClick={() => setLevelFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de nível">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {teamLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Equipe: {teamLabel}
              <button onClick={() => setTeamFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de equipe">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={openBulkChangeLevel} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}>
            <Icon name="shield" className="w-3.5 h-3.5" /> Alterar nível
          </button>
          <button onClick={bulkResendInvite} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}>
            <Icon name="mail" className="w-3.5 h-3.5" /> Reenviar convite
          </button>
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}>
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}>
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }}>Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar usuários: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhum usuário encontrado"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie o primeiro usuário para começar'}
              actionLabel="Novo usuário"
              onAction={() => navigate('/settings/users/create')}
            />
          )}

          {/* Grid (Cards) — F34: cards SEM checkbox, seleção em massa só na tabela */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))] gap-4">
              {sortedItems.map((user: UserResponse) => (
                <UserCard
                  key={user.id}
                  user={user}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onAvatarChange={handleAvatarChange} onChangeLevel={openChangeLevel}
                  onResendInvite={handleResendInvite}
                  onResetPassword={handleResetPassword}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((user: UserResponse) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                    onAvatarChange={handleAvatarChange} onChangeLevel={openChangeLevel}
                  onResendInvite={handleResendInvite}
                  onResetPassword={handleResetPassword}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        ariaLabel="Selecionar todos"
                        data-testid="users-select-all"
                      />
                      <SortableHeadCell<SortKey> label="Usuário" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Código" sortKey="code" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Documento" />
                      <SortableHeadCell<SortKey> label="Nível" sortKey="level" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Equipes" />
                      <SortableHeadCell<SortKey> label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Último acesso" sortKey="lastLogin" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((user: UserResponse) => (
                      <UserListItem
                        key={user.id}
                        user={user}
                        selected={selectedIds.has(user.id)}
                        onToggleSelect={toggleSelect}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                        onAvatarChange={handleAvatarChange} onChangeLevel={openChangeLevel}
                  onResendInvite={handleResendInvite}
                  onResetPassword={handleResetPassword}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="usuários"
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="users-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      {/* Mini-modal: alterar nível (usuário único ou em massa) */}
      <Modal isOpen={!!levelModal} onClose={() => setLevelModal(null)}>
        <ModalHeader onClose={() => setLevelModal(null)}>
          {levelModal?.bulk ? `Alterar nível de ${selectedCount} usuário(s)` : 'Alterar nível'}
        </ModalHeader>
        <ModalContent>
          <Select
            label="Novo nível"
            value={levelValue}
            onChange={setLevelValue}
            options={levels.map((l) => ({ value: l.id, label: l.name }))}
            placeholder="Selecione o nível"
          />
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setLevelModal(null)}>Cancelar</Button>
          <Button variant="primary" onClick={confirmChangeLevel} disabled={!levelValue || updateUser.isPending}>Aplicar</Button>
        </ModalFooter>
      </Modal>

      {/* Confirmação de exclusão (single) */}
      <DeleteConfirmationModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        message="Tem certeza que deseja deletar este usuário?"
        isLoading={deleteUser.isPending}
      />

      {/* Confirmação de exclusão (bulk) */}
      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        message={`Excluir ${selectedCount} usuário(s) selecionado(s)?`}
        isLoading={deleteUser.isPending}
      />
    </div>
  );
};
